(ns dashboard.entity.questionnaire.db
  (:require [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]
            [dashboard.ui :as ui]
            [io.repliance.questionnaire.utils :refer [index-attachment attachments-to-answers-index]]
            [re-frame.core :as rf]))

;; ----------------------------------------------------------------------------
;;  Configuration
;; ----------------------------------------------------------------------------

(def default-page 0)

(def default-page-size 100)

(def initial-form-state {:response "" :additional-information ""})

(def question-status-options
  [["approved" {:label "Approved"}]
   ["in-review" {:label "In Review"}]
   [nil {:label "Unknown"}]])

(def status->question-status-options
  (into {} question-status-options))

;; ----------------------------------------------------------------------------
;;  Subscriptions and Callers
;; ----------------------------------------------------------------------------

(rf/reg-sub
 ::questionnaire-attachments
 (fn [db [_ questionnaire-id]]
   (get-in db [:questionnaires questionnaire-id :attachments :data])))

(defn questionnaire-attachments*
  [questionnaire-id]
  (rf/subscribe [::questionnaire-attachments questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::answer-attachments
 (fn [db [_ answer-id]]
   (get-in db [:indexed-attachments answer-id])))

(defn answer-attachments*
  [answer-id]
  (rf/subscribe [::answer-attachments answer-id]))

;; ----------------

(rf/reg-sub
  ::questionnaire
  (fn [db [_ questionnaire-id]]
    (get-in db [:questionnaires questionnaire-id])))

(defn questionnaire*
  [questionnaire-id]
  (rf/subscribe [::questionnaire questionnaire-id]))

;; ----------------

(defn get-sections
  [db questionnaire-id]
  (sort-by :section-name
           (set (map #(select-keys % [:section-id
                                      :section-name])
                     (get-in db [:questionnaires
                                 questionnaire-id
                                 :questions
                                 :data])))))


(defn follow-ups
  "Find follow-up nodes with the given parent-id"
  [parent-id rows]
  (keep (fn [row]
          (when (= (:parent-id row) parent-id)
            ;; If a node has a parent with the parent-id,
            ;; then recursively find its follow-ups
            (assoc row :follow-ups (follow-ups (:id row) rows))))
        rows))

(defn make-trees
  "Keep only the root nodes (node with parent-id nil) and fill follow-ups for all roots."
  [rows]
  (keep (fn [{:keys [parent-id id] :as question}]
          ;; In the row list, if a node has a parent, then it is not a root
          (when-not parent-id
            ;; If a node is a root, then find it's follow-ups
            (assoc question :follow-ups (follow-ups id rows))))
        rows))

;; ----------------------------------------------------------------------------
;;  Effects and Events Handlers
;; ----------------------------------------------------------------------------

(rf/reg-event-fx
 ::set-question-status
 (fn [{:keys [db]} [_ questionnaire-id question-id status]]
   {:db (update-in db [:questionnaires questionnaire-id :questions :data]
                   (fn [data]
                     (map (fn [question]
                            (if (= question-id (:id question))
                              (assoc question :status status)
                              question))
                          data)))
    :dispatch [::notifications/add {:message "Question status updated"}]}))

(rf/reg-event-fx
 ::update-question-status
 (fn [_ [_ questionnaire-id question-id status]]
   {:http-xhrio {:method :put
                 :uri (str "/api/questions/question/" question-id "/status")
                 :params {:status status}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 ;; XXX Would there be some worth in having the status
                 ;; roundtripped from the backend instead of just using what
                 ;; we have here already? [Vesa M.]
                 :on-success [::set-question-status questionnaire-id question-id status]
                 :on-failure [::ui/set-error "Failed to updated status of question"]}}))

(defn update-question-status
  [questionnaire-id question-id status]
  (rf/dispatch [::update-question-status questionnaire-id question-id status]))

;; ----------------

(rf/reg-event-fx
 ::get-questions-success
 (fn [{:keys [db]} [_ after questionnaire questions]]
   (cond-> {:db (let [first-section-by-name (:section-id (first (sort-by :section-name questions)))]
                  (-> db
                      (assoc-in [:questionnaires questionnaire :questions] {:loading false
                                                                            :load-success true
                                                                            :data questions})
                      (assoc-in [:page :questionnaire-detail :current-section] first-section-by-name)))}
     after (assoc :dispatch after))))

(rf/reg-event-fx
 ::get-questions-failure
 (fn [{:keys [db]} [_ after questionnaire]]
   {:db (update-in db
                   [:questionnaires questionnaire :questions]
                   assoc
                   :loading false
                   :load-success false)
    :fx (cond-> [[:dispatch [::notifications/add {:message "Failed to get questions"
                                                  :class "is-danger"}]]]
          after (conj [:dispatch after]))}))

(rf/reg-event-fx
 ::get-questions
 (fn [{:keys [db]} [_ questionnaire after]]
   {:db (assoc-in db [:questionnaires questionnaire :questions] {:loading true
                                                                 :load-success false
                                                                 :data nil})
    :http-xhrio {:method :get
                 :uri (str "/api/questionnaires/" questionnaire "/questions")
                 :response-format http-config/transit-response-format
                 :on-success [::get-questions-success (:success after) questionnaire]
                 :on-failure [::get-questions-failure (:failure after) questionnaire]}}))

(defn get-questions
  [questionnaire after]
  (rf/dispatch [::get-questions questionnaire after]))

;; ----------------

(rf/reg-event-db
 ::set-attachments
 (fn [db [_ questionnaire attachments]]
   (let [attachment-index (attachments-to-answers-index attachments)]
     (-> db
         (assoc :indexed-attachments attachment-index)
         (assoc-in [:questionnaires questionnaire :attachments] {:loading false
                                                                 :load-success false
                                                                 :data attachments})))))

(rf/reg-event-fx
 ::get-attachments
 (fn [{:keys [db]} [_ questionnaire]]
   {:db (assoc-in db [:questionnaires questionnaire :attachments] {:loading true
                                                                   :load-success false
                                                                   :data nil})
    :http-xhrio {:method :get
                 :uri (str "/api/questionnaires/" questionnaire "/attachments/")
                 :response-format http-config/transit-response-format
                 :on-success [::set-attachments questionnaire]
                 :on-failure [::ui/set-error "Failed to get attachments"]}}))

(defn get-attachments
  [questionnaire]
  (rf/dispatch [::get-attachments questionnaire]))

;; ----------------

(rf/reg-event-fx
 ::get-metadata-success
 (fn [{:keys [db]} [_ after questionnaire metadata]]
   (cond-> {:db (assoc-in db [:questionnaires questionnaire :metadata] {:loading false
                                                                        :load-success true
                                                                        :data metadata})}
     after (assoc :dispatch after))))

(rf/reg-event-fx
 ::get-metadata-failure
 (fn [{:keys [db]} [_ after questionnaire]]
   {:db (assoc-in db [:questionnaires questionnaire :metadata] {:loading false
                                                                :load-success false})
    :fx (cond-> [[:dispatch [::notifications/add {:message "Failed to get metadata"}]]]
          after (conj [:dispatch after]))}))

(rf/reg-event-fx
 ::get-metadata
 (fn [{:keys [db]} [_ questionnaire after]]
   {:db (assoc-in db [:questionnaires questionnaire :metadata] {:loading true
                                                                :load-success false
                                                                :data nil})
    :http-xhrio {:method :get
                 :uri (str "/api/questionnaires/" questionnaire "/metadata")
                 :response-format http-config/transit-response-format
                 :on-success [::get-metadata-success (:success after) questionnaire]
                 :on-failure [::get-metadata-failure (:failure after) questionnaire]}}))

(defn get-metadata
  [questionnaire after]
  (rf/dispatch [::get-metadata questionnaire after]))

;; ----------------

(rf/reg-event-db
 ::link-answer-to-attachment-success
 (fn [db [_ questionnaire-id {:keys [item-id document-id] :as attachment} {:keys [answer-id]}]]
   (let [updated-attachment (update attachment :linked-answers (comp set conj) answer-id)]
     (-> db
         (assoc-in [:page :policies :item-statuses document-id :attaching] false)
         (update-in [:indexed-attachments answer-id] (comp set conj) updated-attachment)
         (update-in [:questionnaires questionnaire-id :attachments :data]
                    (fn [attachments]
                      (if (seq (filter #(= item-id (:item-id %)) attachments))
                        (map #(if (= item-id (:item-id %))
                                updated-attachment
                                %)
                             attachments)
                        (conj attachments updated-attachment))))))))
(rf/reg-event-fx
 ::link-answer-to-attachment-fail
 (fn [{:keys [db]} [_ {:keys [document-id]}]]
   {:db (assoc-in db [:page :policies :item-statuses document-id :attaching] false)
    :dispatch [::ui/set-error "Could not link document to answer"]}))

(rf/reg-event-fx
 ::link-answer-to-attachment
 (fn [{:keys [db]} [_ questionnaire-id answer-id question-id {:keys [item-id document-id] :as attachment}]]
   {:db (assoc-in db [:page :policies :item-statuses document-id :attaching] true)
    :http-xhrio {:method          :post
                 :uri             (str "/api/questionnaires/"
                                       questionnaire-id
                                       "/attachments/"
                                       (subs item-id (count "ATT__"))
                                       "/linked-answers")
                 :params          {:answer-id answer-id
                                   :question-id question-id}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::link-answer-to-attachment-success questionnaire-id attachment]
                 :on-failure      [::link-answer-to-attachment-fail attachment]}}))

(defn link-answer-to-attachment
  [questionnaire-id answer-id question-id attachment]
  (rf/dispatch [::link-answer-to-attachment questionnaire-id answer-id question-id attachment]))

;; ----------------

(rf/reg-event-db
 ::unlink-answer-from-attachment-success
 (fn [db [_ questionnaire-id answer-id {:keys [item-id document-id] :as attachment}]]
   (-> db
       (assoc-in [:page :policies :item-statuses document-id :attaching] false)
       (update-in [:indexed-attachments answer-id]
                  (fn [attachments]
                    (set (remove #(= (:item-id %) (:item-id attachment))
                                 attachments))))
       (update-in [:questionnaires questionnaire-id :attachments :data]
                  (fn [attachments]
                    (map (fn [attachment]
                           (if (= (:item-id attachment) item-id)
                             (update attachment :linked-answers disj answer-id)
                             attachment))
                         attachments))))))

(rf/reg-event-fx
 ::unlink-answer-from-attachment-fail
 (fn [{:keys [db]} [_ questionnaire-id answer-id {:keys [document-id] :as attachment} {:keys [status]}]]
   {:db (assoc-in db [:page :policies :item-statuses document-id :attaching] false)
    :dispatch (if (= status 202) ; hack due to re-frame-http-fx failing on HTTP 202
                [::unlink-answer-from-attachment-success questionnaire-id answer-id attachment]
                [::ui/set-error "Could not unlink document from answer"])}))

(rf/reg-event-fx
 ::unlink-answer-from-attachment
 (fn [{:keys [db]} [_ questionnaire-id question-id answer-id {:keys [document-id item-id] :as attachment}]]
   {:db (assoc-in db [:page :policies :item-statuses document-id :attaching] true)
    :http-xhrio {:method          :delete
                 ;; TODO: Use (str/replace item-id "ATT__" "") instead of subs+count
                 :uri             (str "/api/questionnaires/"
                                       questionnaire-id
                                       "/attachments/"
                                       (subs item-id (count "ATT__"))
                                       "/linked-answers")
                 :params          {:answer-id answer-id
                                   :question-id question-id}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::unlink-answer-from-attachment-success questionnaire-id answer-id attachment]
                 :on-failure      [::unlink-answer-from-attachment-fail questionnaire-id answer-id attachment]}}))

(defn unlink-answer-from-attachment
  [questionnaire-id question-id answer-id attachment]
  (rf/dispatch [::unlink-answer-from-attachment questionnaire-id question-id answer-id attachment]))

;; ----------------

(rf/reg-event-fx
 ::add-attachment-success
 (fn [{:keys [db]} [_ questionnaire-id {:keys [document-id filename] :as attachment}]]
   {:db (-> db
            (assoc-in [:page :policies :item-statuses document-id :attaching] false)
            (assoc :indexed-attachments (index-attachment (get db :indexed-attachments) attachment))
            (update-in [:questionnaires questionnaire-id :attachments :data] conj attachment))
    :dispatch [::notifications/add {:message (str "Attached document " filename)}]}))

(rf/reg-event-fx
 ::add-attachment-fail
 (fn [{:keys [db]} [_ {:keys [document-id]}]]
   {:db (assoc-in db [:page :policies :item-statuses document-id :attaching] false)
    :dispatch [::ui/set-error "Could not attach document"]}))

(rf/reg-event-fx
 ::add-attachment
 (fn [{:keys [db]} [_ questionnaire-id {:keys [document-id] :as attachment}]]
   {:db (assoc-in db [:page :policies :item-statuses document-id :attaching] true)
    :http-xhrio {:method          :post
                 :uri             (str "/api/questionnaires/"
                                       questionnaire-id
                                       "/attachments")
                 :params          attachment
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::add-attachment-success questionnaire-id]
                 :on-failure      [::add-attachment-fail attachment]}}))

(defn add-attachment
  [questionnaire-id attachment]
  (rf/dispatch [::add-attachment questionnaire-id attachment]))

;; ----------------

(rf/reg-event-fx
 ::delete-attachment-failure
 (fn [{:keys [db]} [_ {:keys [item-id] :as attachment} questionnaire-id {:keys [status]}]]
   (if (= status 202) ; hack due to re-frame-http-fx failing on HTTP 202
     {:dispatch [::delete-attachment-success attachment questionnaire-id]}
     {:db       (assoc-in db [:page :questionnaire-detail :details :attachments item-id :deleting?] false)
      :dispatch [::ui/set-error "Failed to delete attachment"]})))

(rf/reg-event-db
 ::delete-attachment-success
 (fn [db [_ {:keys [item-id document-id]} questionnaire-id]]
   (-> db
       (assoc-in [:page :policies :item-statuses document-id :detaching?] false)
       (update-in [:questionnaires questionnaire-id :attachments :data]
                  (fn [attachments]
                    (remove (fn [attachment]
                              (= (:item-id attachment) item-id))
                            attachments))))))

(rf/reg-event-fx
 ::delete-attachment
 (fn [{:keys [db]} [_ questionnaire-id {:keys [item-id document-id] :as attachment}]]
   {:db         (-> db
                    (assoc-in [:page :policies :item-statuses document-id :detaching?] true)
                    (assoc-in [:page :questionnaire-detail :details :attachments item-id :deleting?] true))
    :http-xhrio {:method          :delete
                 :uri             (str "/api/questionnaires/" questionnaire-id "/attachments/" (subs item-id (count "ATT__")))
                 :params          {}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::delete-attachment-success attachment questionnaire-id]
                 :on-failure      [::delete-attachment-failure attachment questionnaire-id]}}))

(defn delete-attachment
  [questionnaire-id attachment]
  (rf/dispatch [::delete-attachment questionnaire-id attachment]))

;; -----------

;; OBSOLETE AND FORGOTTEN SUBS

;; #_(rf/reg-sub
;;    ::sections-from-questions
;;    (fn [db [_ questionnaire-id]]
;;      (get-sections db questionnaire-id)))
;;
;; #_(rf/reg-sub
;;    ::current-section
;;    (fn [db [_ questionnaire-id]]
;;      (get-in db [:page :questionnaire-detail :current-section] (get-sections db questionnaire-id))))
;;
;; #_(rf/reg-sub
;;   ::current-question
;;   (fn [db _]
;;     (get-in db [:page :questionnaire-detail :current-question])))
;;
;; #_(rf/reg-sub
;;   ::question-status-frequencies
;;   (fn [db [_ questionnaire-id]]
;;     (let [questions (get-in db [:questionnaires questionnaire-id :questions :data])]
;;       [(frequencies (map (fn [question]
;;                           (:status question))
;;                          questions))
;;        (count questions)])))
;;
;; #_(rf/reg-sub
;;   ::pagination
;;   (fn [db [_ questionnaire-id question-filter-fn]]
;;     (let [pagination (get-in db [:page :questionnaire-detail :pagination])
;;           questions-matching-filter (filter question-filter-fn (get-in db [:questionnaires questionnaire-id :questions :data]))
;;           page (get pagination :page default-page)
;;           page-size (get pagination :page-size default-page-size)]
;;       {:page page
;;        :page-size page-size
;;        :page-count (js/Math.ceil (/ (count questions-matching-filter)
;;                                     page-size))
;;        :page-questions (->> (sort-by :ordinal questions-matching-filter)
;;                             (drop (* page page-size))
;;                             (take page-size))})))
;;
;; ;; Returns a map of question id to a set of sub-question statuses, that can be quickly checked
;; ;; against
;; ;;
;; ;; e.g. {#uuid "a071571b-325e-4569-b510-a7f8cb33805c" #{in-review nil},
;; ;;       #uuid "1051c56d-e776-4ea3-a755-bbd72fd14dde" #{in-review}}
;; #_(rf/reg-sub
;;  ::question-follow-up-status-index
;;  (fn [db [_ questionnaire-id]]
;;    (let [questions (get-in db [:questionnaires questionnaire-id :questions :data])]
;;      (reduce (fn [index question]
;;                (if-let [parent-id (:parent-id question)]
;;                  (update index parent-id (comp set conj) (:status question))
;;                  index))
;;              {}
;;              questions))))
;;
;; #_(rf/reg-sub
;;    ::answer-state
;;    (fn [db [_ question]]
;;      (get-in db
;;              [:page :questionnaire-detail :answer-state question]
;;              initial-form-state)))
;;
;; #_(rf/reg-sub
;;    ::answer-save-status
;;    (fn [db [_ answer-id]]
;;      (get-in db [:page :questionnaire-detail :detail :answer-statuses answer-id] false)))

;; ----------------
;; OBSOLETE AND UNUSED EVENTS

;; #_(rf/reg-event-db
;;  ::set-current-question
;;  (fn [db [_ question-id]]
;;    (assoc-in db [:page :questionnaire-detail :current-question] question-id)))
;;
;; #_#_#_(rf/reg-event-fx
;;        ::set-questionnaire-status
;;        (fn [{:keys [db]} [_ questionnaire status]]
;;          {:db (assoc-in db
;;                         [:questionnaires questionnaire :metadata :data :status]
;;                         status)
;;           :dispatch [::notifications/add {:message "Questionnaire status updated"}]}))
;;
;;     (rf/reg-event-fx
;;      ::update-questionnaire-status
;;      (fn [_ [_ {:keys [questionnaire status]}]]
;;        {:http-xhrio {:method :put
;;                      :uri (str "/api/questionnaires/" questionnaire "/status")
;;                      :params {:status status}
;;                      :format http-config/transit-request-format
;;                      :response-format http-config/transit-response-format
;;                  ;; XXX Would there be some worth in having the status
;;                  ;; roundtripped from the backend instead of just using what
;;                  ;; we have here already? [Vesa M.]
;;                      :on-success [::set-questionnaire-status questionnaire status]
;;                      :on-failure [::ui/set-error "Failed to updated status of questionnaire"]}}))
;;
;;   (defn update-questionnaire-status
;;     [questionnaire status]
;;     (rf/dispatch [::update-questionnaire-status questionnaire status]))
;;
;; #_(rf/reg-event-fx
;;    ::select-answer
;;    (fn [_ [_ questionnaire-id question-id answer-id]]
;;      {:http-xhrio {:method :put
;;                    :uri (str "/api/questions/question/" question-id "/answer")
;;                    :params {:answer-id answer-id}
;;                    :format http-config/transit-request-format
;;                    :response-format http-config/transit-response-format
;;                    :on-success [::set-answer-selected questionnaire-id question-id answer-id]
;;                    :on-failure [::ui/set-error "Failed to select answer"]}}))
;;
;; #_(rf/reg-event-fx
;;    ::set-answer-selected
;;    (fn [{:keys [db]} [_ questionnaire-id question-id answer-id]]
;;      (let [answer (first (filter #(= (:id %) answer-id)
;;                                  (get-in db [:questionnaires questionnaire-id :answers-by-question question-id :data])))
;;            db (update-in db [:questionnaires questionnaire-id :questions :data]
;;                          (fn [questions]
;;                            (mapv (fn [question]
;;                                    (if (= (:id question) question-id)
;;                                      (assoc question :selected-answer answer)
;;                                      question))
;;                                  questions)))]
;;        {:db db
;;         :dispatch [::notifications/add {:message "Answer selected"}]})))
;;
;; #_(rf/reg-event-fx
;;    ::set-answer-form-state
;;    (fn [{:keys [db]} [_ question-id state initial?]]
;;      (let [info (get-in db [:page :questionnaire-detail :answer-state question-id :additional-information])]
;;        (if (and initial?
;;                 (not (str/blank? info))
;;                 (not= info (:additional-information state)))
;;          {:dispatch [::ui/push-modal
;;                      {:ctx       {:on-confirm (fn [] (rf/dispatch [::set-answer-form-state question-id state]))}
;;                       :component #'overwrite-answer/confirm-overwrite-answer}]}
;;          {:db (assoc-in db [:page :questionnaire-detail :answer-state question-id] state)}))))
;;
;; #_(rf/reg-event-fx
;;    ::edit-answer
;;    (fn [{:keys [db]} [_ questionnaire-id question-id answer-id selected-answer? attribute attribute-value]]
;;      (let [answer (if selected-answer?
;;                     (:selected-answer (first (filter (fn [question]
;;                                                        (= (:id question) question-id))
;;                                                      (get-in db [:questionnaires questionnaire-id :questions :data]))))
;;                     (first (filter (fn [answer]
;;                                      (= (:id answer) answer-id))
;;                                    (get-in db [:questionnaires questionnaire-id :answers-by-question question-id :data]))))
;;            new-answer (assoc answer attribute attribute-value)]
;;        {:db db
;;         :http-xhrio {:method          :post
;;                      :uri             (str "/api/answers/answer/" answer-id)
;;                      :params          (select-keys new-answer [:question-id :response :additional-information :options]) ;; TODO: options editing not actually supported yet
;;                      :format          http-config/transit-request-format
;;                      :response-format http-config/transit-response-format
;;                      :on-success      [::edit-answer-success questionnaire-id question-id answer-id selected-answer?]
;;                      :on-failure      [::edit-answer-fail answer-id]}})))
;;
;; #_(rf/reg-event-db
;;    ::edit-answer-success
;;    (fn [db [_ questionnaire-id question-id answer-id selected-answer? {:keys [response additional-information options]}]]
;;      (let [db (update-in db [:questionnaires questionnaire-id :answers-by-question question-id :data] (fn [answers]
;;                                                                                                         (mapv (fn [answer]
;;                                                                                                                 (if (= (:id answer) answer-id)
;;                                                                                                                   (assoc answer
;;                                                                                                                          :response response
;;                                                                                                                          :additional-information additional-information
;;                                                                                                                          :options options)
;;                                                                                                                   answer))
;;                                                                                                               answers)))
;;            db (if selected-answer?
;;                 (update-in db [:questionnaires questionnaire-id :questions :data] (fn [questions]
;;                                                                                     (mapv (fn [question]
;;                                                                                             (if (= (:id question) question-id)
;;                                                                                               (update question :selected-answer (fn [answer]
;;                                                                                                                                   (assoc answer
;;                                                                                                                                          :response response
;;                                                                                                                                          :additional-information additional-information
;;                                                                                                                                          :options options)))
;;                                                                                               question))
;;                                                                                           questions)))
;;                 db)]
;;        (assoc-in db [:page :questionnaire-detail :detail :answer-statuses answer-id] false))))
;;
;; #_(rf/reg-event-fx
;;    ::edit-answer-fail
;;    (fn [{:keys [db]} [_ answer-id]]
;;      {:db       (update-in db [:page :questionnaire-detail :detail :answer-statuses] dissoc answer-id)
;;       :dispatch [::ui/set-error "Could not update answer"]}))
;;
;; #_(rf/reg-event-fx
;;    ::create-and-select-answer
;;    (fn [_ [_ questionnaire-id question-id answer]]
;;      {:http-xhrio {:method :post
;;                    :uri (str "/api/questions/question/" question-id "/answer")
;;                    :params answer
;;                    :format http-config/transit-request-format
;;                    :response-format http-config/transit-response-format
;;                    :on-success [::set-answer-created-and-selected questionnaire-id question-id]
;;                    :on-failure [::ui/set-error "Failed to create and select answer"]}}))
;;
;; #_(rf/reg-event-fx
;;    ::set-answer-created-and-selected
;;    (fn [{:keys [db]} [_ questionnaire-id question-id answer]]
;;      {:db (-> db
;;               (update-in [:questionnaires questionnaire-id :answers-by-question question-id :data] conj answer)
;;               (update-in [:questionnaires questionnaire-id :questions :data] (fn [questions]
;;                                                                                (mapv (fn [question]
;;                                                                                        (if (= (:id question) (:question-id answer))
;;                                                                                          (assoc question :selected-answer answer)
;;                                                                                          question))
;;                                                                                      questions)))
;;               (assoc-in [:page :questionnaire-detail :details :answer-state question-id] initial-form-state))
;;       :dispatch [::notifications/add {:message "Answer created and selected"}]}))
;;
;;
;;
;;
;;
;; #_(rf/reg-event-db
;;    ::set-current-section
;;    (fn  [db [_ first-section-by-name]]
;;      (assoc-in db [:page :questionnaire-detail :current-section] first-section-by-name)))
;;
;; #_(rf/reg-fx
;;    ::scroll-into-view!
;;    (fn [[scroll-target]]
;;      (.scrollIntoView scroll-target (js-obj "behavior" "smooth"))))
;;
;; #_(rf/reg-event-fx
;;    ::set-pagination-page
;;    (fn [{:keys [db]} [_ page scroll-target]]
;;      {:db (assoc-in db [:page :questionnaire-detail :pagination :page] page)
;;       ::scroll-into-view! [scroll-target]}))
;;
;; #_(rf/reg-event-db
;;    ::set-pagination-page-size
;;    (fn [db [_ page-size]]
;;      (assoc-in db [:page :questionnaire-detail :pagination :page-size] page-size)))
;;
;; ;; ----------------